import { useEffect, useState } from 'react'
import { executiveApi, type ExecutiveFilters, type ExecutiveRevenueCommission, type RevenueCommissionRow } from '../api/executive'
import { useFeatureFlag } from '../lib/featureFlags'
import { Callout, Card, CardContent, CardHeader, DataTable, DataTableEmptyRow, LoadingState } from './ui'

/**
 * R3-18 - vista unica di ricavi e provvigioni nel cockpit Direzione (flag `report.ricavi_provvigioni`): ricavo presunto,
 * maturato e incassato accanto a provvigione presunta, maturata e pagata, per agente e per pratica. Solo con la lettura
 * amministrativa; senza, il pannello non compare.
 */

const eur = (n: number) => n.toLocaleString('it-IT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 })

function Table({ title, first, rows }: { title: string; first: string; rows: RevenueCommissionRow[] }) {
  return (
    <Card padding="none">
      <CardHeader title={title} subtitle="Ricavo e provvigione nel periodo, affiancati" />
      <CardContent>
        <DataTable variant="plain">
          <thead>
            <tr>
              <th>{first}</th>
              <th className="text-end">Ricavo presunto</th>
              <th className="text-end">Ricavo maturato</th>
              <th className="text-end">Ricavo incassato</th>
              <th className="text-end">Provv. presunta</th>
              <th className="text-end">Provv. maturata</th>
              <th className="text-end">Provv. pagata</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.key}>
                <td>{r.label}</td>
                <td className="text-end md-num">{eur(r.revenue.presumed)}</td>
                <td className="text-end md-num">{eur(r.revenue.accrued)}</td>
                <td className="text-end md-num">{eur(r.revenue.collected)}</td>
                <td className="text-end md-num">{eur(r.commission.presumed)}</td>
                <td className="text-end md-num">{eur(r.commission.accrued)}</td>
                <td className="text-end md-num">{eur(r.commission.paid)}</td>
              </tr>
            ))}
            {rows.length === 0 && <DataTableEmptyRow colSpan={7} title="Nessun dato nel periodo." />}
          </tbody>
        </DataTable>
      </CardContent>
    </Card>
  )
}

export function RevenueCommissionPanel({ filters }: { filters: ExecutiveFilters }) {
  const flagOn = useFeatureFlag('report.ricavi_provvigioni')
  const [data, setData] = useState<ExecutiveRevenueCommission | null>(null)
  const [error, setError] = useState('')

  useEffect(() => {
    if (!flagOn) return
    let alive = true
    setData(null)
    setError('')
    executiveApi.revenueCommission(filters)
      .then(d => { if (alive) setData(d) })
      .catch(e => { if (alive) setError(e instanceof Error ? e.message : 'Errore') })
    return () => { alive = false }
  }, [flagOn, filters])

  if (!flagOn) return null
  if (error) return <Callout tone="warning">Ricavi e provvigioni non disponibili. {error}</Callout>
  if (!data) return <LoadingState />
  if (!data.enabled || !data.money) return null

  return (
    <>
      <Table title="Ricavi e provvigioni per agente" first="Agente" rows={data.byAgent ?? []} />
      <Table title="Ricavi e provvigioni per pratica" first="Pratica" rows={data.byContract ?? []} />
      {data.limitations && (
        <ul className="md-typescale-body-small text-muted mb-0">
          {data.limitations.map(l => <li key={l}>{l}</li>)}
        </ul>
      )}
    </>
  )
}
